import { EventDispatcher } from 'three';

/**
 * AssetLibrary allows loading a queue of assets.
 */
export default class Asset3DLibrary extends EventDispatcher {
  /**
   * Creates a new AssetLibrary object.
   * @param basePath The root directory under which all assets are located. Any filename used when queueing assets is
   * relative to this.
   */
  constructor(basePath) {
    super();

    basePath = basePath || './';

    // make sure the path ends with a slash if it doesn't already
    if (basePath.charAt(basePath.length - 1) !== '/') basePath += '/';

    this.basePath = basePath;
    this.numLoaded = 0;
    this.numAssets = 0;
    this.queue = [];
    this.assets = {};
  }

  /**
   * Adds an asset to the load queue.
   * @param id The ID that can be used to fetch the assets from the library once loaded.
   * @param filename The filename (relative to the base path passed in the constructor) of the asset.
   * @param loaderType The type of loader to use. Fe: TextureLoader, FBXLoader, ...
   */
  queueAsset(id, filename, loaderType) {
    // using full url, should not prepend pase path.
    if (filename.indexOf('http:') < 0 && filename.indexOf('https:') < 0)
      filename = this.basePath + filename;

    this.queue.push({
      id,
      filename,
      loader: new loaderType(),
    });

    ++this.numAssets;
  }

  /**
   * Triggers loading of all assets. Once this is called, no assets should be queued.
   */
  load() {
    const queueLength = this.queue.length;
    for (let i = 0; i < queueLength; i++) {
      const assetDef = this.queue.pop();
      const onLoaded = asset => {
        this.assets[assetDef.id] = asset;
        this._onAssetLoaded(asset);

        if (this.numLoaded === this.numAssets) {
          this.dispatchEvent({ type: 'complete' });
        }
      };

      assetDef.loader.load(
        assetDef.filename,
        onLoaded,
        this._onAssetProgress.bind(this),
        this._onAssetError.bind(this)
      );
    }
  }

  /**
   * Retrieves a loaded asset.
   * @param id The asset's ID as defined when adding to the queue.
   */
  get(id) {
    return this.assets[id];
  }

  /**
   * Explicitly set an asset with a given id.
   * @param id The asset's ID as defined when adding to the queue.
   * @param value The asset to associate with the ID.
   */
  set(id, value) {
    this.assets[id] = value;
  }

  _onAssetLoaded(asset) {
    this.numLoaded++;
    this.dispatchEvent({
      type: 'loaded',
      asset,
      progress: this.numLoaded / this.numAssets,
    });
  }

  _onAssetProgress(event) {
    const ratio = event.loaded / event.total;
    this.dispatchEvent({
      type: 'progress',
      progress: (this.numLoaded + ratio) / this.numAssets,
    });
  }

  _onAssetError(error) {
    this.dispatchEvent({
      type: 'error',
      error,
    });
  }
}
